import {
  useContext,
  useState,
  useCallback,
  useEffect,
  ChangeEvent
} from 'react';
import {
  InputCheckboxGroup,
  InputCheckbox,
  InputTextField
} from '@abbott-components/core';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import BlockIcon from '@mui/icons-material/Block';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ControlState from '../../Shared/controlState';
import {
  WorkflowEngineContext,
  WorkflowEngineActionKind
} from '../../WorkflowEngine/workflowEngineReducer';
import { AppContext } from '../../App/appReducer';
import { SelectOption, SelectSubtypes, Disposition } from './types';
import { useFormContext } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { getErrorMessage } from '../../Shared/errorHandling';
import { get } from '../../Shared/ajax';
import SelectOptionEngine from './SelectOptionsEngine';
import formatName from '../formatName';
import SizingSelect from '../../Shared/SizingSelect';
import { OptionOrientation } from '../../Shared/Enums/optionOrientation';
import { GridSize } from '../../Shared/Enums/gridSize';

interface GetFormDispositions {
  dispositions: Disposition[];
}

interface ErrorObjectAttribute {
  message: string;
}

interface ErrorObjectShape {
  value: ErrorObjectAttribute;
  id: ErrorObjectAttribute;
}

export default (control: ControlState) => {
  const { state, dispatch } = useContext(WorkflowEngineContext);
  const { isReadOnlyView, workflowType, isStatusEnabled } = state;

  const { state: appState } = useContext(AppContext);
  const { workflowId } = appState;
  const { schema } = control;

  const properties = schema?.properties as JSONSchema7Properties;

  const { enqueueSnackbar } = useSnackbar();
  const formContext = useFormContext();

  const subTypeFieldName = formatName(properties['subType']?.$id as string);
  const optionsFieldname = formatName(properties['options']?.$id as string);
  const optionOrientationFieldname = formatName(
    properties['optionOrientation']?.$id as string
  );

  const statusEnabledName = formatName(
    properties['statusEnabled']?.$id as string
  );

  const utilizeHiddenComponentsFieldName = formatName(
    properties['utilizeHiddenComponents']?.$id as string
  );

  const optionState = formContext.getFieldState(optionsFieldname);

  const isStatusSelectionEnabled = (): boolean => {
    return formContext.getValues(statusEnabledName) as boolean;
  };

  const isUtilizeHiddenComponentsEnabled = (): boolean => {
    return formContext.getValues(utilizeHiddenComponentsFieldName) as boolean;
  };

  const [uHCCheckbox, setuHCCheckbox] = useState<boolean>(
    isUtilizeHiddenComponentsEnabled()
  );
  const handleUtilizeHiddenComponentsChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setuHCCheckbox(event.target.checked);
    formContext.setValue(
      utilizeHiddenComponentsFieldName,
      event.target.checked
    );
  };

  const isConditionalEnabled = (): boolean => {
    return (control.schema?.properties as JSONSchema7Properties)['conditional']
      ?.default as boolean;
  };
  const [subTypeOption, setSubTypeOption] = useState(
    formContext.getValues(subTypeFieldName)
  );

  const [controlDispositions, setControlDispositions] = useState<
    Disposition[] | null
  >(null);

  const [statusCheckbox, setStatusCheckbox] = useState<boolean>(
    isStatusSelectionEnabled()
  );
  const [gridSizeSelected, setGridSizeSelected] = useState<GridSize>(
    properties['gridSize']?.default as GridSize
  );
  const isStatusFeatureSupported = (status: SelectSubtypes) =>
    [SelectSubtypes.MULTI_CHECKBOXES, SelectSubtypes.MULTI_DROPDOWN].includes(
      status
    );

  const handleSelectTypeChange = (event: SelectChangeEvent) => {
    formContext.setValue(subTypeFieldName, event.target.value);
    setSubTypeOption(event.target.value);

    if (
      isStatusFeatureSupported(event.target.value as SelectSubtypes) &&
      isStatusSelectionEnabled()
    ) {
      formContext.setValue(statusEnabledName, false);
      setStatusCheckbox(false);
      setStatusEnabledInWorkflowContext(false);
    }
  };

  const toggleStatusEnableForControl = (ctrl: ControlState) => {
    const ctrlProperties = ctrl.schema?.properties as JSONSchema7Properties;
    const currentValue = ctrlProperties['statusEnabled']?.default as boolean;

    ctrlProperties['statusEnabled'].default = !currentValue;

    dispatch({
      type: WorkflowEngineActionKind.CONTROL_UPDATE,
      payload: {
        ...ctrl,
        schema: {
          ...ctrl.schema,
          properties: ctrlProperties
        }
      }
    });
  };

  const setStatusEnabledInWorkflowContext = (value: boolean) => {
    dispatch({
      type: WorkflowEngineActionKind.SET_STATUS_ENABLE,
      payload: value
    });
  };

  const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    formContext.setValue(statusEnabledName, event.target.checked);
    setStatusCheckbox(event.target.checked);
    setStatusEnabledInWorkflowContext(event.target.checked);
    toggleStatusEnableForControl(control);
  };

  const updateOptions = useCallback((newOptions: SelectOption[]) => {
    formContext.setValue(optionsFieldname, newOptions);
  }, []);

  const updateOptionOrientation = useCallback(
    (newOptionOreintation: OptionOrientation) => {
      formContext.setValue(optionOrientationFieldname, newOptionOreintation);
    },
    []
  );

  const renderSelectTypeOptions = () => {
    return Object.values(SelectSubtypes).map((value) => {
      return (
        <MenuItem value={value} key={`SelectorTypeOption-${value}`}>
          {value}
        </MenuItem>
      );
    });
  };

  const renderHiddenField = () => {
    return (
      <>
        <InputTextField
          name={formatName(properties['options']?.$id as string)}
          label="Hidden Options"
          isDisabled={isReadOnlyView}
          sx={{ display: 'none' }}
        />
        <InputTextField
          name={formatName(properties['statusEnabled']?.$id as string)}
          label="Hidden Status"
          isDisabled={isReadOnlyView}
          sx={{ display: 'none' }}
        />
      </>
    );
  };

  const evaluateErrors = () => {
    const result = { isError: false, message: '' };

    if (Array.isArray(optionState.error)) {
      result.isError = true;
      const errors: string[] = [];
      optionState.error.map((e: ErrorObjectShape) => {
        const message = e.value ? e.value.message : e.id ? e.id.message : '';
        if (message.length > 0 && errors.indexOf(message) === -1) {
          errors.push(message);
        }
      });
      result.message = errors.join();
    } else if (optionState?.error?.message) {
      result.isError = true;
      result.message = optionState?.error.message;
    }

    return result;
  };

  const renderValidationMessage = () => {
    const { isError, message } = evaluateErrors();

    if (isError) {
      return (
        <Typography variant="body1" sx={{ fontSize: '14px', color: '#e4002b' }}>
          {message}
        </Typography>
      );
    }

    return null;
  };

  const renderCheckboxIcon = () => {
    return isCheckboxDisabled() && !isConditionalEnabled() ? (
      <BlockIcon />
    ) : (
      <CheckBoxOutlineBlankIcon />
    );
  };

  const asyncRetrieveDispositions = async () => {
    try {
      const response = await get<GetFormDispositions>(
        `DynamicWorkflowService/Dispositions?dynamicWorkflowExternalId=${workflowId}&workflowType=${workflowType}`
      );
      if (response && response.dispositions)
        setControlDispositions(response.dispositions);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const isCheckboxDisabled = () => {
    let result = false;

    if (isReadOnlyView) {
      result = true;
    } else if (isStatusSelectionEnabled()) {
      result = false;
    } else {
      result =
        isStatusFeatureSupported(subTypeOption) ||
        isStatusEnabled ||
        isConditionalEnabled();
    }

    return result;
  };

  const getTooltipMessage = () => {
    if (isStatusSelectionEnabled()) return '';

    if (isStatusEnabled)
      return 'You can only have one control with status selection';

    if (isStatusFeatureSupported(subTypeOption))
      return 'Status selection is available only for single-select type';

    return '';
  };
  const sizingSelectOnChange = (size: string) => {
    setGridSizeSelected(size as unknown as GridSize);
  };
  useEffect(() => {
    asyncRetrieveDispositions();
  }, []);

  return (
    <Stack direction="column" spacing={3}>
      <Stack direction="row" spacing={3}>
        <Stack sx={{ flexGrow: 4 }}>
          <InputTextField
            name={formatName(properties['label']?.$id as string)}
            label="Select Label"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <Stack sx={{ flexGrow: 4 }}>
          <InputTextField
            name={formatName(properties['id']?.$id as string)}
            label="ID"
            isDisabled={isReadOnlyView}
          />
        </Stack>
        <SizingSelect
          isReadOnlyView={isReadOnlyView}
          name={formatName(properties['gridSize']?.$id as string)}
          sizingSelectOnChange={sizingSelectOnChange}
        />
        <Stack sx={{ flexGrow: 3, paddingTop: '16px', width: '150px' }}>
          <FormControl disabled={isReadOnlyView}>
            <InputLabel>Select Type</InputLabel>
            <Select
              id={formatName(properties['subType']?.$id as string)}
              label="Select Type"
              value={subTypeOption}
              onChange={handleSelectTypeChange}
            >
              {renderSelectTypeOptions()}
            </Select>
          </FormControl>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={6} justifyContent="flex-start">
        <Stack sx={{ paddingBottom: '16px' }}>
          <InputCheckboxGroup
            name={`${formatName(
              properties['required']?.$id as string
            )}-inputCheckBoxGroup`}
            isDisabled={isReadOnlyView}
          >
            <InputCheckbox
              name={formatName(properties['required']?.$id as string)}
              defaultChecked={
                (properties['required'].default as boolean) ?? false
              }
            >
              Required Field
            </InputCheckbox>
          </InputCheckboxGroup>
        </Stack>
        <Stack sx={{ paddingBottom: '16px' }}>
          <FormGroup>
            <Tooltip title={getTooltipMessage()} placement="bottom-start">
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={isCheckboxDisabled()}
                    checked={statusCheckbox}
                    onChange={handleChangeCheckbox}
                    icon={renderCheckboxIcon()}
                    checkedIcon={<CheckBoxIcon />}
                  />
                }
                label="Enable Status Selection"
              />
            </Tooltip>
          </FormGroup>
        </Stack>
        <Stack sx={{ paddingBottom: '16px' }}>
          <InputCheckboxGroup
            name={`${utilizeHiddenComponentsFieldName}-inputCheckBoxGroup`}
            isDisabled={isReadOnlyView}
          >
            <InputCheckbox
              name={utilizeHiddenComponentsFieldName}
              defaultChecked={uHCCheckbox}
              onCheckboxChange={handleUtilizeHiddenComponentsChange}
            >
              Utilize Hidden Components
            </InputCheckbox>
          </InputCheckboxGroup>
        </Stack>
      </Stack>
      <Stack direction="row">
        <Stack>
          <Typography variant="body1">Options</Typography>
        </Stack>
        <Stack sx={{ flexGrow: 2, display: 'flex', justifyContent: 'center' }}>
          <Divider variant="middle" />
        </Stack>
      </Stack>
      {renderHiddenField()}
      <SelectOptionEngine
        isReadOnlyView={isReadOnlyView}
        statusEnabled={isStatusSelectionEnabled()}
        utilizeHiddenComponents={uHCCheckbox}
        selectType={subTypeOption}
        initialOptionOrientation={formContext.getValues(
          optionOrientationFieldname
        )}
        initialOptions={formContext.getValues(optionsFieldname)}
        dispositions={controlDispositions}
        updateOptions={updateOptions}
        updateOptionOrientation={updateOptionOrientation}
        renderValidation={renderValidationMessage}
        gridSize={gridSizeSelected}
      />
    </Stack>
  );
};
